// src/pages/horario_alumno.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

// Columnas (días)
const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

// Ventana y recesos para construir filas dinámicas
const WINDOW_START = '07:00';
const WINDOW_END   = '15:00';
const RECESOS = [
  ['10:00', '10:30'],
  ['12:30', '12:45'],
];

// Helpers de tiempo
const hhmm = (t) => (t || '').slice(0, 5); // 'HH:MM:SS' -> 'HH:MM'
const overlaps = (aIni, aFin, bIni, bFin) => (aIni < bFin) && (aFin > bIni);

// Construye filas (renglones) dinámicos a partir de ventana, recesos y clases
function buildFilas(horarios) {
  const cuts = new Set([WINDOW_START, WINDOW_END]);
  for (const [ri, rf] of RECESOS) { cuts.add(ri); cuts.add(rf); }
  (horarios || []).forEach(h => { cuts.add(hhmm(h.horaInicio)); cuts.add(hhmm(h.horaFin)); });

  const sorted = Array.from(cuts).sort();
  const filas = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const ini = sorted[i], fin = sorted[i + 1];
    if (ini >= fin) continue;

    const esReceso = RECESOS.some(([ri, rf]) => overlaps(ini, fin, ri, rf));
    filas.push({ ini, fin, label: `${ini} - ${fin}`, receso: esReceso });
  }
  return filas;
}

// Helper: convertir imagen a dataURL para jsPDF.addImage
async function imgToDataURL(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = src;
  });
}

export default function HorarioAlumno() {
  const [grupo, setGrupo] = useState(null);     // {IdGrupo, Grado, CicloEscolar}
  const [horarios, setHorarios] = useState([]); // [{dia,horaInicio,horaFin,materia,aula}]
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const alumnoNombre = localStorage.getItem('displayName') || 'Alumno';
  const idUsuario = localStorage.getItem('idUsuario');

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  useEffect(() => {
    (async () => {
      try {
        if (!idUsuario) {
          setErr('No se encontró idUsuario en la sesión.');
          setLoading(false);
          return;
        }
        // 1) Grupo del alumno
        const gData = await fetchJsonWithFallback(`/horarios/alumno/grupo?idUsuario=${encodeURIComponent(idUsuario)}`);
        setGrupo(gData || null);

        // 2) Horario del alumno
        const hData = await fetchJsonWithFallback(`/horarios/alumno/mi-horario?idUsuario=${encodeURIComponent(idUsuario)}`);
        const adaptado = (hData || []).map(r => ({
          dia: r.DiaSemana,
          horaInicio: hhmm(r.HoraInicio),
          horaFin: hhmm(r.HoraFin),
          materia: r.Materia || '',
          aula: r.Aula || ''
        }));
        setHorarios(adaptado);
      } catch (e) {
        console.error(e);
        setErr('No fue posible cargar tu horario.');
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchJsonWithFallback, idUsuario]);

  // Filas dinámicas para pintar la tabla y exportar
  const FILAS = useMemo(() => buildFilas(horarios), [horarios]);

  // PDF (usa las mismas filas dinámicas)
  const exportarPDF = async () => {
    const doc = new jsPDF('p', 'pt');
    const margenX = 40;

    try {
      const imgLeft = await imgToDataURL(logoInstituto);
      doc.addImage(imgLeft, 'PNG', margenX, 28, 52, 52);
      // Si quieres el de la derecha:
      // const imgRight = await imgToDataURL(logoSEP);
      // doc.addImage(imgRight, 'PNG', doc.internal.pageSize.getWidth() - margenX - 52, 28, 52, 52);
    } catch {}

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('Instituto Silviano Carrillo', doc.internal.pageSize.getWidth() / 2, 50, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Consulta de Horarios del Alumno', doc.internal.pageSize.getWidth() / 2, 68, { align: 'center' });

    // Cajita superior
    const cajaTop = 90;
    const cajaAlt = 70;
    doc.setDrawColor(230);
    doc.setFillColor(247, 247, 247);
    doc.roundedRect(margenX, cajaTop, doc.internal.pageSize.getWidth() - margenX * 2, cajaAlt, 6, 6, 'FD');

    const cicloEscolar = grupo?.CicloEscolar || '2024-2025';
    const leyendaHorario = 'Ventana 07:00–15:00';

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`ALUMNO: ${alumnoNombre}`, margenX + 14, cajaTop + 26);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('HORARIO DE PRIMARIA', margenX + 14, cajaTop + 64);
    doc.text(cicloEscolar, margenX + 170, cajaTop + 64);
    doc.text(leyendaHorario, doc.internal.pageSize.getWidth() / 2, cajaTop + cajaAlt + 22, { align: 'center' });

    // Tabla dinámica
    const head = [['Hora', ...DIAS]];
    const body = FILAS.map((f) => {
      if (f.receso) {
        return [
          { content: f.label, styles: { fontStyle: 'bold' } },
          { content: 'RECESO', colSpan: DIAS.length, styles: { halign: 'center', fontStyle: 'bold', fillColor: [240, 240, 240] } }
        ];
      }
      const fila = [f.label];
      DIAS.forEach(d => {
        const c = horarios.find(h => h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin));
        fila.push(c ? `${c.materia}${c.aula ? `\nAula ${c.aula}` : ''}` : '');
      });
      return fila;
    });

    autoTable(doc, {
      head,
      body,
      startY: cajaTop + cajaAlt + 40,
      styles: { halign: 'center', valign: 'middle', fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [230, 230, 230] },
      columnStyles: { 0: { cellWidth: 110, fontStyle: 'bold' } },
      theme: 'grid',
      margin: { left: margenX, right: margenX },
    });

    doc.save(`horario_alumno_${alumnoNombre.replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Consulta de Horarios del Alumno</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Tarjeta superior (como la maqueta) */}
      <div className="p-4 bg-light rounded shadow-sm text-center">
        <div className="d-flex justify-content-center mb-3">
          <span className="fw-bold">PRIMARIA PARTICULAR INCORPORADA SEP</span>
          <span className="mx-3"></span>
          <span className="fw-bold">CLAVE: 14PR0155G</span>
        </div>
        <div className="d-flex justify-content-center mb-3">
          <span>HORARIO DE PRIMARIA</span>
          <span className="mx-3"></span>
          <span>{grupo?.CicloEscolar || '2024-2025'}</span>
        </div>
        <div className="d-flex justify-content-center">
          <span>HORARIO DE 7:20 A 2:50</span>
        </div>
      </div>

      {/* Tabla */}
      <div className="p-4 bg-light rounded shadow-sm mt-4">
        <h5 className="text-center">Horario de Clases</h5>

        {loading && <div className="text-center py-3">Cargando…</div>}
        {err && !loading && <div className="alert alert-danger text-center">{err}</div>}

        {!loading && !err && (
          <>
            <table className="table table-bordered text-center align-middle" id="tablaHorarios">
              <thead>
                <tr>
                  <th style={{ width: '120px' }}>Hora</th>
                  {DIAS.map(d => <th key={d}>{d}</th>)}
                </tr>
              </thead>
              <tbody>
                {FILAS.map((f) => (
                  <tr key={f.label}>
                    <td className="fw-semibold">{f.receso ? `${f.label} (RECESO)` : f.label}</td>

                    {f.receso ? (
                      <td colSpan={DIAS.length} className="table-secondary fw-bold">Receso</td>
                    ) : (
                      DIAS.map((d) => {
                        const celda = horarios.find(h =>
                          h.dia === d && overlaps(f.ini, f.fin, h.horaInicio, h.horaFin)
                        );
                        return (
                          <td key={`${f.label}-${d}`}>
                            {celda ? (
                              <>
                                <div className="fw-semibold">{celda.materia}</div>
                                {celda.aula ? <small className="text-muted">Aula {celda.aula}</small> : null}
                              </>
                            ) : null}
                          </td>
                        );
                      })
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Botón PDF */}
            <div className="d-flex justify-content-end mt-3">
              <button className="btn btn-outline-danger" onClick={exportarPDF}>
                Exportar a PDF
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

